import React, { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { dataService } from '@/lib/dataService'
import { useAuth } from '@/contexts/AuthContext'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'

export const ScoringGrid: React.FC = () => {
  const { assignmentId: paramId } = useParams<{ assignmentId: string }>()
  const assignmentId = Number(paramId)
  const { user } = useAuth()

  const [assignment, setAssignment] = useState<any>(null)
  const [session, setSession] = useState<any>(null)
  const [subject, setSubject] = useState<any>(null)
  const [classroom, setClassroom] = useState<any>(null)
  const [students, setStudents] = useState<any[]>([])
  
  // Local score state: { [studentId]: { pre: string, post: string } }
  const [scores, setScores] = useState<Record<number, { pre: string; post: string }>>({})
  const [submission, setSubmission] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  
  // Filtering
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<'grid' | 'analytics'>('grid')

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])
  
  // Save status states
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [confirmSubmit, setConfirmSubmit] = useState<'pre' | 'post' | null>(null)

  // Unlock Request features
  const [showUnlockModal, setShowUnlockModal] = useState<'pre' | 'post' | null>(null)
  const [unlockReason, setUnlockReason] = useState('')
  const isReadOnly = user?.role !== 'admin' && assignment && user?.id !== assignment?.teacher_id
  const isHOD = user?.role === 'head_of_department'
  const [unlockRequests, setUnlockRequests] = useState<any[]>([])

  // Google Integration Features (Sheets Backup & Gmail messaging)
  const [backingUp, setBackingUp] = useState(false)
  const [showMailModal, setShowMailModal] = useState<any>(null)
  const [emailTarget, setEmailTarget] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)

  const backupToGoogleSheets = async (scoresList?: any[]) => {
    const token = localStorage.getItem('google_drive_access_token')
    if (!token) {
      alert('🔌 บัญชี Google ยังไม่ได้รับการเชื่อมต่อ กรุณากดปุ่ม "เชื่อมบัญชี Google" ที่แถบเมนูด้านบนของหน้าจอ')
      return
    }

    setBackingUp(true)
    try {
      // 1. Find or create "คลังข้อสอบ TesScore" folder
      let folderId = ''
      const searchFolderUrl = `https://www.googleapis.com/drive/v3/files?q=name='คลังข้อสอบ TesScore' and mimeType='application/vnd.google-apps.folder' and trashed=false&fields=files(id)`
      const folderRes = await fetch(searchFolderUrl, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const folderData = await folderRes.json()
      
      if (folderData.files && folderData.files.length > 0) {
        folderId = folderData.files[0].id
      } else {
        const createFolderRes = await fetch('https://www.googleapis.com/drive/v3/files', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: 'คลังข้อสอบ TesScore',
            mimeType: 'application/vnd.google-apps.folder'
          })
        })
        const newFolder = await createFolderRes.json()
        folderId = newFolder.id
      }

      // 2. Find or create "สำรองคะแนน TesScore" sheet
      let spreadsheetId = ''
      const searchSheetUrl = `https://www.googleapis.com/drive/v3/files?q=name='สำรองคะแนน TesScore' and mimeType='application/vnd.google-apps.spreadsheet' and '${folderId}' in parents and trashed=false&fields=files(id)`
      const sheetRes = await fetch(searchSheetUrl, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const sheetData = await sheetRes.json()

      if (sheetData.files && sheetData.files.length > 0) {
        spreadsheetId = sheetData.files[0].id
      } else {
        const createSheetRes = await fetch('https://www.googleapis.com/drive/v3/files', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: 'สำรองคะแนน TesScore',
            mimeType: 'application/vnd.google-apps.spreadsheet',
            parents: [folderId]
          })
        })
        const newSheet = await createSheetRes.json()
        spreadsheetId = newSheet.id
      }

      // 3. Define tab title based on subject and classroom
      const gradeText = classroom ? `ม.${classroom.grade_level >= 7 ? classroom.grade_level - 6 : classroom.grade_level}/${classroom.room_number}` : '—'
      const sheetTitle = `${subject?.code || 'วิชา'} - ${gradeText}`

      // Create new tab sheet if not existing
      try {
        await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            requests: [
              {
                addSheet: {
                  properties: {
                    title: sheetTitle
                  }
                }
              }
            ]
          })
        })
      } catch (e) {
        // Tab sheet already exists
      }

      // 4. Prepare values
      const currentScores = scoresList || students.map(st => {
        const s = scores[st.id] || { pre: '', post: '' }
        return {
          student_id: st.id,
          pre_test_score: s.pre !== '' ? Number(s.pre) : null,
          post_test_score: s.post !== '' ? Number(s.post) : null,
        }
      })

      const timestamp = new Date().toLocaleString('th-TH')
      const values = [
        ['วันเวลาที่สำรองข้อมูล', 'รหัสประจำตัวนักเรียน', 'ชื่อ-นามสกุล', 'คะแนน Pre-Test', 'คะแนน Post-Test', 'ความก้าวหน้า', 'ผู้บันทึก'],
        ...students.map(st => {
          const item = currentScores.find((c: any) => c.student_id === st.id)
          const pre = item?.pre_test_score !== undefined && item?.pre_test_score !== null ? item.pre_test_score : '-'
          const post = item?.post_test_score !== undefined && item?.post_test_score !== null ? item.post_test_score : '-'
          const progress = (pre !== '-' && post !== '-') ? (Number(post) - Number(pre)) : '-'
          return [
            timestamp,
            st.student_id_card,
            `${st.first_name} ${st.last_name}`,
            pre,
            post,
            progress,
            user ? `${user.first_name} ${user.last_name}` : 'ครูผู้สอน'
          ]
        })
      ]

      // 5. Overwrite the sheet tab range
      const writeUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetTitle)}!A1:G${values.length}?valueInputOption=USER_ENTERED`
      const writeRes = await fetch(writeUrl, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ values })
      })

      if (writeRes.ok) {
        alert(`💾 สำรองข้อมูลวิชา ${subject?.code} ห้อง ${gradeText} ลง Google Sheets สำเร็จแล้วในโฟลเดอร์ "คลังข้อสอบ TesScore"`)
      } else {
        throw new Error('เขียนข้อมูลลง Google Sheets ไม่สำเร็จ')
      }
    } catch (err: any) {
      alert(`❌ การสำรองข้อมูลคะแนนดิบล้มเหลว: ${err.message || 'เครือข่ายผิดพลาด'}`)
      console.error(err)
    } finally {
      setBackingUp(false)
    }
  }

  const handleOpenMailModal = (student: any) => {
    const s = scores[student.id] || { pre: '', post: '' }
    const progress = s.pre !== '' && s.post !== '' ? (Number(s.post) - Number(s.pre)) : null
    const gradeText = classroom ? `ม.${classroom.grade_level >= 7 ? classroom.grade_level - 6 : classroom.grade_level}/${classroom.room_number}` : '—'
    
    setEmailTarget(student.email || '')
    setEmailSubject(`รายงานผลคะแนนสอบวิชา ${subject?.name} (${subject?.code}) - ${student.first_name} ${student.last_name}`)
    setEmailBody(
      `เรียน นักเรียนและผู้ปกครองของ ${student.first_name} ${student.last_name}\n\n` +
      `ระบบ TesScore ขอส่งรายงานผลคะแนนสอบ Pre-Test และ Post-Test รายบุคคลดังนี้:\n` +
      `- วิชา: ${subject?.name} (${subject?.code})\n` +
      `- ห้องเรียน: ${gradeText}\n` +
      `- คะแนนสอบก่อนเรียน (Pre-Test): ${s.pre !== '' ? s.pre : '-'} คะแนน (เต็ม ${subject?.max_score_pre})\n` +
      `- คะแนนสอบหลังเรียน (Post-Test): ${s.post !== '' ? s.post : '-'} คะแนน (เต็ม ${subject?.max_score_post})\n` +
      `- ความก้าวหน้า (พัฒนาการ): ${progress !== null ? (progress > 0 ? '+' + progress : progress) : '-'} คะแนน\n\n` +
      `ผู้บันทึกคะแนน: ${user?.first_name} ${user?.last_name}\n` +
      `ระบบส่งรายงานอัตโนมัติผ่าน Google Workspace Integration`
    )
    setShowMailModal(student)
  }

  const sendGmailNotification = async () => {
    const token = localStorage.getItem('google_drive_access_token')
    if (!token) {
      alert('🔌 บัญชี Google ยังไม่ได้รับการเชื่อมต่อ กรุณากดเชื่อมต่อด้านบนของหน้าเว็บ')
      return
    }

    if (!emailTarget) {
      alert('⚠️ กรุณาระบุอีเมลปลายทาง')
      return
    }

    setSendingEmail(true)

    try {
      // Build MIME message
      const utf8Subject = `=?utf-8?B?${btoa(unescape(encodeURIComponent(emailSubject)))}?=`;
      const emailContent = [
        `To: ${emailTarget}`,
        `Subject: ${utf8Subject}`,
        `MIME-Version: 1.0`,
        `Content-Type: text/plain; charset=utf-8`,
        `Content-Transfer-Encoding: 7bit`,
        ``,
        emailBody
      ].join('\r\n')

      // Safe Base64URL encoding
      const base64SafeEmail = btoa(unescape(encodeURIComponent(emailContent)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')

      const res = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          raw: base64SafeEmail
        })
      })

      if (res.ok) {
        alert(`📧 ส่งจดหมายแจ้งผลคะแนนไปยัง ${emailTarget} สำเร็จเรียบร้อยแล้ว!`)
        setShowMailModal(null)
      } else {
        const errJson = await res.json()
        throw new Error(errJson.error?.message || 'ส่งจดหมายไม่สำเร็จ')
      }
    } catch (err: any) {
      alert(`❌ ส่งอีเมลล้มเหลว: ${err.message}`)
    } finally {
      setSendingEmail(false)
    }
  }
  
  // Custom Excel Export Modal States
  const [currentPage, setCurrentPage] = useState(1)
  const [showExportModal, setShowExportModal] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState({
    studentCode: true,
    no: true,
    name: true,
    scorePre: true,
    percentPre: true,
    scorePost: true,
    percentPost: true,
    gain: true,
    gainPercent: true,
    evaluation: true
  })

  // Ref to track latest scores for auto-save debounce
  const scoresRef = useRef(scores)
  const debounceTimerRef = useRef<any>(null)

  useEffect(() => {
    scoresRef.current = scores
  }, [scores])

  // Force analytics tab for head of department (read-only summary view)
  useEffect(() => {
    if (isHOD) setActiveTab('analytics')
  }, [isHOD])

  // Load Scoring Data
  const loadScoringData = async () => {
    if (!user || isNaN(assignmentId)) return
    setLoading(true)
    try {
      const allAssignments = await dataService.getTeachingAssignments()
      const currentAssignment = allAssignments.find((a: any) => a.id === assignmentId)
      if (!currentAssignment) {
        throw new Error('ไม่พบข้อมูลการสอนนี้ในระบบ')
      }

      const [
        allSessions,
        allSubjects,
        allClassrooms,
        classStudents,
        allSubmissions,
        allUnlockRequests
      ] = await Promise.all([
        dataService.getAssessmentSessions(),
        dataService.getSubjects(),
        dataService.getClassrooms(),
        dataService.getStudents(currentAssignment.classroom_id),
        dataService.getScoreSubmissions(),
        dataService.getUnlockRequests()
      ])


      if (user?.role === 'head_of_department') {
        const isAssignedTeacher = currentAssignment.teacher_id === user.id
        const allExamFiles = await dataService.getExamFilesList()
        const isCoTeacherShared = allExamFiles.some((f: any) => 
          f.subject_id === currentAssignment.subject_id &&
          f.tags &&
          f.tags.includes(`co_teacher:${user.id}`)
        )

        if (!isAssignedTeacher && !isCoTeacherShared) {
          alert('🔒 ขออภัย คุณไม่มีสิทธิ์เข้าถึงคะแนนรายบุคคลของรายวิชา/ห้องเรียนนี้ (PDPA Protection)')
          window.location.href = '/department'
          return
        }
      }

      setAssignment(currentAssignment)

      const currentSubject = allSubjects.find((s: any) => s.id === currentAssignment.subject_id)
      const currentClassroom = allClassrooms.find((c: any) => c.id === currentAssignment.classroom_id)
      const activeSession = allSessions.find((s: any) => s.is_open)

      setSubject(currentSubject)
      setClassroom(currentClassroom)
      setSession(activeSession)
      // Sort students by class roll number (เลขที่) ascending
      classStudents.sort((a: any, b: any) => (a.number_in_class || 0) - (b.number_in_class || 0))
      setStudents(classStudents)

      const sub = allSubmissions.find((s: any) => 
        s.teaching_assignment_id === assignmentId && 
        s.assessment_session_id === activeSession?.id
      )
      setSubmission(sub || null)

      // Filter unlock requests for this assignment and session
      if (activeSession) {
        const filteredReqs = allUnlockRequests.filter((r: any) => 
          r.teaching_assignment_id === assignmentId && 
          r.assessment_session_id === activeSession.id
        )
        setUnlockRequests(filteredReqs)
      }

      // Load scores: use active session if open, otherwise use most-recent session (read-only display)
      const sessionForScores = activeSession
        || allSessions.slice().sort((a: any, b: any) =>
            new Date(b.end_date).getTime() - new Date(a.end_date).getTime()
          )[0]
        || null

      if (sessionForScores) {
        const existingScores = await dataService.getScores(
          currentAssignment.subject_id,
          currentAssignment.classroom_id,
          sessionForScores.id
        )

        const scoreMap: Record<number, { pre: string; post: string }> = {}
        classStudents.forEach((st: any) => {
          const match = existingScores.find((sc: any) => sc.student_id === st.id)
          scoreMap[st.id] = {
            pre: match?.pre_test_score !== undefined && match?.pre_test_score !== null ? String(match.pre_test_score) : '',
            post: match?.post_test_score !== undefined && match?.post_test_score !== null ? String(match.post_test_score) : ''
          }
        })
        setScores(scoreMap)

        // If no open session but there is a recent session, use it for display context
        if (!activeSession && sessionForScores) {
          setSession(sessionForScores)
        }
      }
    } catch (err: any) {
      console.error(err)
      setNotification({ type: 'error', message: err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadScoringData()
  }, [user, assignmentId])

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Auto-Save background logic (Debounced)
  const triggerAutoSave = () => {
    if (isReadOnly || isSessionExpired()) return
    setSaveStatus('saving')

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const latestScores = scoresRef.current
        const scoresList = students.map(st => {
          const studentScore = latestScores[st.id]
          return {
            student_id: st.id,
            subject_id: assignment.subject_id,
            academic_year_id: assignment.academic_year_id,
            assessment_session_id: session.id,
            teaching_assignment_id: assignment.id,
            classroom_id: assignment.classroom_id,
            pre_test_score: studentScore?.pre !== '' ? Number(studentScore.pre) : null,
            post_test_score: studentScore?.post !== '' ? Number(studentScore.post) : null,
          }
        })

        // Check validation before auto saving (do not save if there's any invalid score)
        const hasErrors = scoresList.some(item => 
          (item.pre_test_score !== null && item.pre_test_score > subject.max_score_pre) ||
          (item.post_test_score !== null && item.post_test_score > subject.max_score_post)
        )

        if (hasErrors) {
          setSaveStatus('error')
          return
        }

        await dataService.saveScores(scoresList, user.id)
        setSaveStatus('saved')
        setTimeout(() => setSaveStatus('idle'), 2000)
      } catch (err) {
        console.error('Auto save error:', err)
        setSaveStatus('error')
      }
    }, 1500) // 1.5 seconds debounce
  }

  // Handle Score Input
  const handleScoreChange = (studentId: number, type: 'pre' | 'post', value: string) => {
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return

    setScores(prev => {
      const updated = {
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [type]: value
        }
      }
      return updated
    })

    // Trigger auto save on input change
    triggerAutoSave()
  }

  const handleStatusChange = async (studentId: number, status: 'active' | 'resigned' | 'transferred') => {
    try {
      await dataService.updateStudentStatus(studentId, status)
      
      // Update local state to trigger re-renders
      setStudents(prev => prev.map(st => {
        if (st.id === studentId) return { ...st, status }
        return st
      }))

      // Clear local scores if resigned/transferred
      if (status !== 'active') {
        setScores(prev => ({
          ...prev,
          [studentId]: { pre: '', post: '' }
        }))
      }

      setNotification({ type: 'success', message: '📋 อัปเดตสถานะนักเรียนเรียบร้อยแล้ว' })
      setTimeout(() => setNotification(null), 3000)
    } catch (err: any) {
      setNotification({ type: 'error', message: err.message || 'เกิดข้อผิดพลาดในการเปลี่ยนสถานะ' })
    }
  }

  const handleSendUnlockRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!showUnlockModal || !unlockReason || !session || !user) return
    try {
      await dataService.createUnlockRequest(assignmentId, session.id, showUnlockModal, unlockReason, user.id)
      setNotification({ type: 'success', message: '📨 ส่งคำร้องขอปลดล็อกแก้ไขคะแนนเรียบร้อยแล้ว กรุณารอผู้ดูแลระบบอนุมัติ' })
      setShowUnlockModal(null)
      setUnlockReason('')
      loadScoringData()
    } catch (err: any) {
      setNotification({ type: 'error', message: err.message || 'เกิดข้อผิดพลาดในการส่งคำร้อง' })
    }
  }

  const handleExportCSV = () => {
    try {
      const headers = ['เลขที่', 'รหัสประจำตัว', 'ชื่อ-นามสกุล', `คะแนน Pre-Test (เต็ม ${subject?.max_score_pre})`, `คะแนน Post-Test (เต็ม ${subject?.max_score_post})`, 'พัฒนาการสะสม', 'สถานะนักเรียน']
      const rows = students.map((st, index) => {
        const s = scores[st.id] || { pre: '', post: '' }
        const progress = s.pre !== '' && s.post !== '' ? String(Number(s.post) - Number(s.pre)) : '—'
        const statusStr = st.status === 'resigned' ? 'ลาออก' : st.status === 'transferred' ? 'ย้ายห้อง' : 'ปกติ'
        return [
          index + 1,
          `="${st.student_id_card}"`, // Keep leading zeros in Excel
          `"${st.first_name} ${st.last_name}"`,
          s.pre !== '' ? s.pre : '—',
          s.post !== '' ? s.post : '—',
          progress,
          statusStr
        ]
      })

      const csvContent = "\uFEFF" + [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.setAttribute("href", url)
      const displayGrade = classroom ? (classroom.grade_level >= 7 ? classroom.grade_level - 6 : classroom.grade_level) : ''
      link.setAttribute("download", `คะแนน_${subject?.code}_ห้อง_ม.${displayGrade}_${classroom?.room_number}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setNotification({ type: 'success', message: '📥 ส่งออกไฟล์รายงานคะแนนดิบ (CSV) เรียบร้อยแล้ว' })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      setNotification({ type: 'error', message: 'เกิดข้อผิดพลาดในการดาวน์โหลดรายงาน' })
    }
  }

  const handleExportCustomExcel = () => {
    try {
      const headers: string[] = []
      if (selectedColumns.studentCode) headers.push('รหัสประจำตัวนักเรียน')
      if (selectedColumns.no) headers.push('เลขที่')
      if (selectedColumns.name) headers.push('ชื่อ-นามสกุล')
      if (selectedColumns.scorePre) headers.push('คะแนน Pre-Test')
      if (selectedColumns.percentPre) headers.push('ร้อยละ Pre-Test')
      if (selectedColumns.scorePost) headers.push('คะแนน Post-Test')
      if (selectedColumns.percentPost) headers.push('ร้อยละ Post-Test')
      if (selectedColumns.gain) headers.push('พัฒนาการ Learning Gain')
      if (selectedColumns.gainPercent) headers.push('ร้อยละความก้าวหน้า')
      if (selectedColumns.evaluation) headers.push('ผลการประเมิน')

      const maxPre = subject?.max_score_pre || 10
      const maxPost = subject?.max_score_post || 10

      const rows = students.map((st, idx) => {
        const s = scores[st.id] || { pre: '', post: '' }
        const row: string[] = []
        
        const preVal = s.pre !== '' ? Number(s.pre) : null
        const postVal = s.post !== '' ? Number(s.post) : null
        const gainVal = (preVal !== null && postVal !== null) ? (postVal - preVal) : null
        const gainPct = (preVal !== null && postVal !== null) ? ((postVal - preVal) / (maxPost - preVal || 1)) * 100 : null
        
        const prePercent = preVal !== null ? (preVal / maxPre) * 100 : null
        const postPercent = postVal !== null ? (postVal / maxPost) * 100 : null
        
        let evalStr = '—'
        if (postPercent !== null) {
          evalStr = postPercent >= 50 ? 'ผ่านเกณฑ์ (50%)' : 'ไม่ผ่านเกณฑ์'
        }

        if (selectedColumns.studentCode) row.push(`"${st.student_id_card || ''}"`)
        if (selectedColumns.no) row.push(`"${st.no || idx + 1}"`)
        if (selectedColumns.name) row.push(`"${st.first_name} ${st.last_name}"`)
        if (selectedColumns.scorePre) row.push(preVal !== null ? `"${preVal}"` : '""')
        if (selectedColumns.percentPre) row.push(prePercent !== null ? `"${prePercent.toFixed(1)}%"` : '""')
        if (selectedColumns.scorePost) row.push(postVal !== null ? `"${postVal}"` : '""')
        if (selectedColumns.percentPost) row.push(postPercent !== null ? `"${postPercent.toFixed(1)}%"` : '""')
        if (selectedColumns.gain) row.push(gainVal !== null ? `"${gainVal.toFixed(1)}"` : '""')
        if (selectedColumns.gainPercent) row.push(gainPct !== null ? `"${gainPct.toFixed(1)}%"` : '""')
        if (selectedColumns.evaluation) row.push(`"${evalStr}"`)

        return row
      })

      const csvContent = "\uFEFF" + [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.setAttribute("href", url)
      
      const displayGrade = classroom ? (classroom.grade_level >= 7 ? classroom.grade_level - 6 : classroom.grade_level) : ''
      link.setAttribute("download", `รายงานคะแนน_วิชา_${subject?.code}_ห้อง_ม.${displayGrade}_${classroom?.room_number}_กำหนดคอลัมน์เอง.csv`)
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setShowExportModal(false)
      setNotification({ type: 'success', message: '📥 ส่งออกไฟล์ Excel (กำหนดคอลัมน์) เรียบร้อยแล้ว' })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      setNotification({ type: 'error', message: 'เกิดข้อผิดพลาดในการส่งออกรายงาน' })
    }
  }

  // Keyboard Navigation: Enter/Tab loop focuses next input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number, type: 'pre' | 'post') => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const nextIndex = index + 1
      const nextInput = document.getElementById(`input-${type}-${nextIndex}`) as HTMLInputElement | null
      if (nextInput) {
        nextInput.focus()
        nextInput.select()
      }
    }
  }

  const isSessionExpired = () => {
    if (!session) return true
    const now = new Date()
    const end = new Date(session.end_date)
    return now > end || !session.is_open
  }

  // Determine what type of session is currently active (pre / post / none)
  const getSessionType = (): 'pre' | 'post' | 'none' => {
    if (!session || isSessionExpired()) return 'none'
    const name = (session.name || '').toLowerCase()
    if (name.includes('ก่อนเรียน') || name.includes('pre')) return 'pre'
    if (name.includes('หลังเรียน') || name.includes('post')) return 'post'
    return 'none'
  }

  // Pre is locked when: session expired, OR already submitted, OR current session is Post-only
  const isPreLocked = () => {
    if (isSessionExpired()) return true
    if (submission?.pre_test_status === 'submitted') return true
    const sType = getSessionType()
    if (sType === 'post') return true   // Post session → Pre inputs are locked
    return false
  }

  // Post is locked when: session expired, OR already submitted, OR current session is Pre-only
  const isPostLocked = () => {
    if (isSessionExpired()) return true
    if (submission?.post_test_status === 'submitted') return true
    const sType = getSessionType()
    if (sType === 'pre') return true    // Pre session → Post inputs are locked
    return false
  }

  // Input Validation Helper (Real-time border highlight)
  const getValidationError = (studentId: number) => {
    const s = scores[studentId]
    if (!s) return { pre: false, post: false }
    const preVal = s.pre !== '' ? Number(s.pre) : null
    const postVal = s.post !== '' ? Number(s.post) : null

    return {
      pre: preVal !== null && subject && preVal > subject.max_score_pre,
      post: postVal !== null && subject && postVal > subject.max_score_post
    }
  }

  // Verify if entire page has validation errors (disables Submit buttons)
  const hasGlobalErrors = () => {
    return students.some(st => {
      const err = getValidationError(st.id)
      return err.pre || err.post
    })
  }

  // Calculations for Stats Card
  const getStats = () => {
    let preSum = 0, preCount = 0
    let postSum = 0, postCount = 0
    let totalProgress = 0, progressCount = 0

    // Ignore resigned and transferred students in averages and learning gain calculations
    const activeClassStudents = students.filter(st => !st.status || st.status === 'active')

    activeClassStudents.forEach(st => {
      const s = scores[st.id]
      if (s) {
        if (s.pre !== '') {
          preSum += Number(s.pre)
          preCount++
        }
        if (s.post !== '') {
          postSum += Number(s.post)
          postCount++
        }
        if (s.pre !== '' && s.post !== '') {
          totalProgress += (Number(s.post) - Number(s.pre))
          progressCount++
        }
      }
    })

    return {
      preAvg: preCount > 0 ? (preSum / preCount).toFixed(2) : '—',
      postAvg: postCount > 0 ? (postSum / postCount).toFixed(2) : '—',
      avgGain: progressCount > 0 ? (totalProgress / progressCount).toFixed(2) : '—',
    }
  }

  const stats = getStats()

  // Save manual draft
  const handleSaveDraftManual = async () => {
    if (isSessionExpired()) return
    setSaveStatus('saving')
    try {
      const scoresList = students.map(st => {
        const studentScore = scores[st.id]
        return {
          student_id: st.id,
          subject_id: assignment.subject_id,
          academic_year_id: assignment.academic_year_id,
          assessment_session_id: session.id,
          teaching_assignment_id: assignment.id,
          classroom_id: assignment.classroom_id,
          pre_test_score: studentScore?.pre !== '' ? Number(studentScore.pre) : null,
          post_test_score: studentScore?.post !== '' ? Number(studentScore.post) : null,
        }
      })

      // Final check
      for (const item of scoresList) {
        if (item.pre_test_score !== null && item.pre_test_score > subject.max_score_pre) {
          throw new Error(`คะแนนก่อนเรียนของบางคน เกินคะแนนเต็ม (${subject.max_score_pre})`)
        }
        if (item.post_test_score !== null && item.post_test_score > subject.max_score_post) {
          throw new Error(`คะแนนหลังเรียนของบางคน เกินคะแนนเต็ม (${subject.max_score_post})`)
        }
      }

      await dataService.saveScores(scoresList, user.id)
      setSaveStatus('saved')
      setNotification({ type: 'success', message: '💾 บันทึกฉบับร่างเรียบร้อยแล้ว' })
      setTimeout(() => {
        setNotification(null)
        setSaveStatus('idle')
      }, 3000)
    } catch (err: any) {
      setSaveStatus('error')
      setNotification({ type: 'error', message: err.message })
    }
  }

  // Final Submit handler
  const handleSubmitFinal = async (type: 'pre' | 'post') => {
    setConfirmSubmit(null)
    try {
      // First auto save
      const latestScores = scoresRef.current
      const scoresList = students.map(st => {
        const studentScore = latestScores[st.id]
        return {
          student_id: st.id,
          subject_id: assignment.subject_id,
          academic_year_id: assignment.academic_year_id,
          assessment_session_id: session.id,
          teaching_assignment_id: assignment.id,
          classroom_id: assignment.classroom_id,
          pre_test_score: studentScore?.pre !== '' ? Number(studentScore.pre) : null,
          post_test_score: studentScore?.post !== '' ? Number(studentScore.post) : null,
        }
      })

      // Validation check
      const incomplete = students.some(st => {
        const s = latestScores[st.id]
        return !s || s[type] === ''
      })

      if (incomplete) {
        throw new Error(`กรุณากรอกคะแนน ${type === 'pre' ? 'ก่อนเรียน (Pre-Test)' : 'หลังเรียน (Post-Test)'} ของนักเรียนทุกคนให้ครบก่อนยืนยันส่ง`)
      }

      await dataService.saveScores(scoresList, user.id)
      await dataService.submitScores(assignmentId, session.id, type, user.id)
      await loadScoringData()
      setNotification({ type: 'success', message: `✅ ยืนยันส่งคะแนน${type === 'pre' ? 'ก่อนเรียน' : 'หลังเรียน'}เสร็จสิ้น ล็อกข้อมูลถาวรแล้ว` })
      
      // Auto-trigger backup to Google Sheets if connected
      const token = localStorage.getItem('google_drive_access_token')
      if (token) {
        backupToGoogleSheets(scoresList)
      }
    } catch (err: any) {
      setNotification({ type: 'error', message: err.message })
    }
  }

  // Filter students based on search box
  const filteredStudents = students.filter(st => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return true
    return (
      st.first_name.toLowerCase().includes(query) ||
      st.last_name.toLowerCase().includes(query) ||
      st.student_id_card.includes(query)
    )
  })

  // Pagination
  const ITEMS_PER_PAGE = 10
  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / ITEMS_PER_PAGE))
  const paginatedStudents = filteredStudents.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', paddingBottom: '120px' }}>
      
      {/* 1. Header Zone */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <Link to="/teacher" style={{ fontSize: '13px', color: 'var(--primary)', display: 'inline-block', marginBottom: '8px', textDecoration: 'none' }}>← กลับไปหน้าหลัก</Link>
          <h1 style={{ fontSize: '26px', fontWeight: '700', marginBottom: '8px' }}>
            ✏️ บันทึกคะแนน: {subject?.name} ({subject?.code})
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            ชั้นเรียน: <strong>ห้อง {classroom ? `ม.${classroom.grade_level >= 7 ? classroom.grade_level - 6 : classroom.grade_level}/${classroom.room_number}` : '—'}</strong> | 
            คะแนนเต็ม: Pre-Test: {subject?.max_score_pre} คะแนน, Post-Test: {subject?.max_score_post} คะแนน
          </p>
        </div>

        {/* Status badges */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => backupToGoogleSheets()}
            disabled={backingUp}
            className="btn btn-outline"
            style={{ 
              padding: '8px 12px', 
              fontSize: '12px', 
              borderColor: 'var(--primary)',
              color: 'var(--primary)',
              background: 'transparent',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              height: '38px',
              whiteSpace: 'nowrap'
            }}
          >
            {backingUp ? '⏳ กำลังสำรอง...' : '💾 สำรองลง Google Sheet'}
          </button>
          <div className="glassmorphism" style={{ padding: '8px 12px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '130px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>สถานะ Pre-Test</span>
            <span style={{
              color: submission?.pre_test_status === 'submitted' ? 'var(--success)' : 'var(--warning)',
              fontWeight: '700',
              fontSize: '13px'
            }}>
              {submission?.pre_test_status === 'submitted' ? '✓ ส่งแล้ว (Locked)' : '⏳ ดราฟต์ (Draft)'}
            </span>
            {submission?.pre_test_status === 'submitted' && (
              (() => {
                const req = unlockRequests.find(r => r.test_type === 'pre' && r.status === 'pending')
                return req ? (
                  <span style={{ fontSize: '11px', color: 'var(--warning)', fontWeight: '600', display: 'block', marginTop: '2px' }}>⏳ รอแอดมินอนุมัติ</span>
                ) : (
                  <button 
                    type="button" 
                    onClick={() => { setShowUnlockModal('pre'); setUnlockReason(''); }} 
                    style={{ 
                      background: 'none', border: 'none', color: 'var(--primary)', 
                      textDecoration: 'underline', fontSize: '11px', padding: 0, 
                      marginTop: '2px', cursor: 'pointer', textAlign: 'left', fontWeight: '600' 
                    }}
                  >
                    🔓 ขอแก้ไขคะแนน
                  </button>
                )
              })()
            )}
          </div>

          <div className="glassmorphism" style={{ padding: '8px 12px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '130px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>สถานะ Post-Test</span>
            <span style={{
              color: submission?.post_test_status === 'submitted' ? 'var(--success)' : 'var(--warning)',
              fontWeight: '700',
              fontSize: '13px'
            }}>
              {submission?.post_test_status === 'submitted' ? '✓ ส่งแล้ว (Locked)' : '⏳ ดราฟต์ (Draft)'}
            </span>
            {submission?.post_test_status === 'submitted' && (
              (() => {
                const req = unlockRequests.find(r => r.test_type === 'post' && r.status === 'pending')
                return req ? (
                  <span style={{ fontSize: '11px', color: 'var(--warning)', fontWeight: '600', display: 'block', marginTop: '2px' }}>⏳ รอแอดมินอนุมัติ</span>
                ) : (
                  <button 
                    type="button" 
                    onClick={() => { setShowUnlockModal('post'); setUnlockReason(''); }} 
                    style={{ 
                      background: 'none', border: 'none', color: 'var(--primary)', 
                      textDecoration: 'underline', fontSize: '11px', padding: 0, 
                      marginTop: '2px', cursor: 'pointer', textAlign: 'left', fontWeight: '600' 
                    }}
                  >
                    🔓 ขอแก้ไขคะแนน
                  </button>
                )
              })()
            )}
          </div>
        </div>
      </div>

      {/* Score Session Type Banner */}
      {(() => {
        const sType = getSessionType()
        if (isSessionExpired()) {
          return (
            <div style={{
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              borderRadius: '12px',
              padding: '14px 18px',
              color: 'var(--danger)',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '18px' }}>🔴</span>
              <div>
                <strong style={{ display: 'block', marginBottom: '2px' }}>ปิดระบบบันทึกคะแนน</strong>
                <span style={{ fontWeight: '400', fontSize: '13px' }}>ยังไม่มีรอบบันทึกคะแนนที่เปิดอยู่ หรือพ้นกำหนดแล้ว — กรุณาติดต่อผู้ดูแลระบบ</span>
              </div>
            </div>
          )
        }
        if (sType === 'pre') {
          return (
            <div style={{
              background: 'rgba(16, 185, 129, 0.08)',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              borderRadius: '12px',
              padding: '14px 18px',
              color: 'var(--success)',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '18px' }}>🟢</span>
              <div>
                <strong style={{ display: 'block', marginBottom: '2px' }}>รอบบันทึกคะแนนก่อนเรียน (Pre-Test) กำลังเปิดอยู่</strong>
                <span style={{ fontWeight: '400', fontSize: '13px' }}>สามารถบันทึกและแก้ไขได้เฉพาะคอลัมน์ Pre-Test เท่านั้น — Post-Test ถูกล็อกไว้จนกว่าจะเปิดรอบถัดไป</span>
              </div>
            </div>
          )
        }
        if (sType === 'post') {
          return (
            <div style={{
              background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.25)',
              borderRadius: '12px',
              padding: '14px 18px',
              color: '#d97706',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '18px' }}>🟠</span>
              <div>
                <strong style={{ display: 'block', marginBottom: '2px' }}>รอบบันทึกคะแนนหลังเรียน (Post-Test) กำลังเปิดอยู่</strong>
                <span style={{ fontWeight: '400', fontSize: '13px' }}>สามารถบันทึกและแก้ไขได้เฉพาะคอลัมน์ Post-Test เท่านั้น — Pre-Test ถูกล็อกไว้แล้ว</span>
              </div>
            </div>
          )
        }
        return null
      })()}

      {/* Auto save indicator */}
      {saveStatus !== 'idle' && (
        <div style={{
          position: 'fixed',
          top: '85px',
          right: '24px',
          zIndex: 50,
          background: saveStatus === 'saving' ? 'rgba(99, 102, 241, 0.2)' : saveStatus === 'saved' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
          border: `1px solid ${saveStatus === 'saving' ? 'var(--primary)' : saveStatus === 'saved' ? 'var(--success)' : 'var(--danger)'}`,
          padding: '8px 16px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {saveStatus === 'saving' && '⏳ กำลังบันทึกอัตโนมัติ...'}
          {saveStatus === 'saved' && '💾 บันทึกแบบร่างอัตโนมัติแล้ว'}
          {saveStatus === 'error' && '⚠️ บันทึกอัตโนมัติผิดพลาด (คะแนนเกินค่าสูงสุด)'}
        </div>
      )}

      {notification && (
        <div style={{
          background: notification.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${notification.type === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
          borderRadius: '8px',
          padding: '16px',
          color: notification.type === 'success' ? 'var(--success)' : 'var(--danger)',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {notification.message}
        </div>
      )}

      {/* Tab Controls for Score Entry vs Classroom Analytics */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '12px', marginTop: '8px' }}>
        {!isHOD && (
          <button
            type="button"
            onClick={() => setActiveTab('grid')}
            className={`btn ${activeTab === 'grid' ? 'btn-primary' : 'btn-outline'}`}
            style={{ padding: '8px 20px', fontSize: '13px', borderRadius: '20px', fontWeight: '600' }}
          >
            ✏️ ตารางกรอกคะแนน (Score Grid)
          </button>
        )}
        <button
          type="button"
          onClick={() => setActiveTab('analytics')}
          className={`btn ${activeTab === 'analytics' ? 'btn-primary' : 'btn-outline'}`}
          style={{ padding: '8px 20px', fontSize: '13px', borderRadius: '20px', fontWeight: '600' }}
        >
          📊 {isHOD ? 'สรุปผลการเรียน (ภาพรวม)' : 'รายงานวิเคราะห์ห้องเรียน (Classroom Analytics)'}
        </button>
        {isHOD && (
          <span style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: 'var(--warning)', fontWeight: '600', gap: '4px', marginLeft: '8px' }}>
            🔒 โหมดหัวหน้ากลุ่มสาระ — เรียกดูได้เฉพาะสรุปผลเท่านั้น
          </span>
        )}
      </div>

      {activeTab === 'grid' && !isHOD && (
        <>
          {/* 2. Toolbar Zone */}
      <div className="glassmorphism" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '240px' }}>
          <input
            type="text"
            className="input-field"
            placeholder="🔍 ค้นหานักเรียน จากชื่อ นามสกุล หรือเลขประจำตัว..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: '10px 16px' }}
          />
        </div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '500' }}>
          แสดงนักเรียน {filteredStudents.length} จาก {students.length} คน
        </div>
      </div>

      {/* 3. Data Area (Table on Desktop, Cards on Mobile) */}
      
      {/* 3A. DESKTOP VIEW */}
      <div className="desktop-only glassmorphism" style={{ padding: '24px' }}>
        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                <th style={{ width: '70px', textAlign: 'center' }}>เลขที่</th>
                <th style={{ width: '120px' }}>รหัสประจำตัว</th>
                <th>ชื่อ-นามสกุล</th>
                <th style={{ width: '160px', textAlign: 'center' }}>Pre-Test (เต็ม {subject?.max_score_pre})</th>
                <th style={{ width: '160px', textAlign: 'center' }}>Post-Test (เต็ม {subject?.max_score_post})</th>
                <th style={{ width: '160px', textAlign: 'center' }}>สถานะนักเรียน</th>
                <th style={{ width: '110px', textAlign: 'center' }}>ความก้าวหน้า</th>
                <th style={{ width: '120px', textAlign: 'center' }}>ส่งเมลแจ้งผล</th>
              </tr>
            </thead>
            <tbody>
              {paginatedStudents.map((st, index) => {
                const s = scores[st.id] || { pre: '', post: '' }
                const progress = s.pre !== '' && s.post !== '' ? (Number(s.post) - Number(s.pre)) : null
                const err = getValidationError(st.id)
                const isInactive = st.status === 'resigned' || st.status === 'transferred'

                return (
                  <tr key={st.id} style={{ opacity: isInactive ? 0.5 : 1 }}>
                    <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{st.number_in_class || index + 1}</td>
                    <td><code style={{ fontSize: '13px' }}>{st.student_id_card}</code></td>
                    <td><strong>{st.first_name} {st.last_name}</strong></td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        id={`input-pre-${index}`}
                        type="number"
                        inputMode="decimal"
                        step="0.5"
                        value={s.pre}
                        onChange={(e) => handleScoreChange(st.id, 'pre', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'pre')}
                        disabled={isReadOnly || isPreLocked() || isInactive}
                        className="scoring-input"
                        placeholder="--"
                        style={{
                          border: err.pre ? '2px solid var(--danger)' : '1px solid var(--border)',
                          boxShadow: err.pre ? '0 0 8px rgba(239, 68, 68, 0.4)' : 'none',
                          background: (isReadOnly || isPreLocked() || isInactive) ? 'rgba(0,0,0,0.05)' : 'var(--bg-input)',
                          cursor: (isReadOnly || isPreLocked() || isInactive) ? 'not-allowed' : 'text'
                        }}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        id={`input-post-${index}`}
                        type="number"
                        inputMode="decimal"
                        step="0.5"
                        value={s.post}
                        onChange={(e) => handleScoreChange(st.id, 'post', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'post')}
                        disabled={isReadOnly || isPostLocked() || isInactive}
                        className="scoring-input"
                        placeholder="--"
                        style={{
                          border: err.post ? '2px solid var(--danger)' : '1px solid var(--border)',
                          boxShadow: err.post ? '0 0 8px rgba(239, 68, 68, 0.4)' : 'none',
                          background: (isReadOnly || isPostLocked() || isInactive) ? 'rgba(0,0,0,0.05)' : 'var(--bg-input)',
                          cursor: (isReadOnly || isPostLocked() || isInactive) ? 'not-allowed' : 'text'
                        }}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <select
                        value={st.status || 'active'}
                        onChange={(e) => handleStatusChange(st.id, e.target.value as any)}
                        disabled={isReadOnly}
                        style={{
                          padding: '6px 10px',
                          background: (isReadOnly) ? 'rgba(0,0,0,0.05)' : st.status === 'resigned' ? 'rgba(239, 68, 68, 0.1)' : st.status === 'transferred' ? 'rgba(245, 158, 11, 0.1)' : 'var(--bg-input)',
                          border: `1px solid ${st.status === 'resigned' ? 'var(--danger)' : st.status === 'transferred' ? 'var(--warning)' : 'var(--border)'}`,
                          color: st.status === 'resigned' ? 'var(--danger)' : st.status === 'transferred' ? 'var(--warning)' : 'var(--text-primary)',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: isReadOnly ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="active">🟢 ปกติ</option>
                        <option value="resigned">🔴 ลาออก</option>
                        <option value="transferred">🟡 ย้ายห้อง</option>
                      </select>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: '700' }}>
                      {progress !== null ? (
                        <span style={{ color: progress > 0 ? 'var(--success)' : progress < 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>
                          {progress > 0 ? `+${progress}` : progress}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handleOpenMailModal(st)}
                        className="btn btn-outline"
                        style={{ padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap' }}
                      >
                        📧 ส่งเมล
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Desktop Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '10px 20px', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              แสดงนักเรียน {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredStudents.length)} จาก {filteredStudents.length} คน
            </span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn btn-outline"
                style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '8px' }}
              >
                ◀ ย้อนกลับ
              </button>
              <span style={{ fontSize: '13px', fontWeight: '600', padding: '0 8px' }}>
                หน้า {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-outline"
                style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '8px' }}
              >
                ถัดไป ▶
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 3B. MOBILE VIEW */}
      <div className="mobile-only" style={{ display: 'none', flexDirection: 'column', gap: '16px' }}>
        {paginatedStudents.map((st, index) => {
          const s = scores[st.id] || { pre: '', post: '' }
          const progress = s.pre !== '' && s.post !== '' ? (Number(s.post) - Number(s.pre)) : null
          const err = getValidationError(st.id)
          const isInactive = st.status === 'resigned' || st.status === 'transferred'

          return (
            <div key={st.id} className="glassmorphism" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', opacity: isInactive ? 0.6 : 1 }}>
              
              {/* Mobile Card Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ color: 'var(--primary)', fontSize: '12px', fontWeight: '600', display: 'block' }}>เลขที่ {st.number_in_class || index + 1}</span>
                  <span style={{ fontSize: '16px', fontWeight: '700' }}>{st.first_name} {st.last_name}</span>
                </div>
                <code style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{st.student_id_card}</code>
              </div>

              {/* Mobile Student Status Selector */}
              <div>
                <label className="input-label" style={{ fontSize: '11px', marginBottom: '4px' }}>สถานะนักเรียน</label>
                <select
                  value={st.status || 'active'}
                  onChange={(e) => handleStatusChange(st.id, e.target.value as any)}
                  className="input-field"
                  style={{
                    height: '42px',
                    background: st.status === 'resigned' ? 'rgba(239, 68, 68, 0.1)' : st.status === 'transferred' ? 'rgba(245, 158, 11, 0.1)' : 'var(--bg-input)',
                    border: `1px solid ${st.status === 'resigned' ? 'var(--danger)' : st.status === 'transferred' ? 'var(--warning)' : 'var(--border)'}`,
                    color: st.status === 'resigned' ? 'var(--danger)' : st.status === 'transferred' ? 'var(--warning)' : 'var(--text-primary)',
                    fontWeight: '600',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="active">🟢 ปกติ (Active)</option>
                  <option value="resigned">🔴 ลาออก (Resigned)</option>
                  <option value="transferred">🟡 ย้ายห้อง (Transferred)</option>
                </select>
              </div>

              {/* Mobile Inputs Layout */}
              <div className="grid-responsive-2" style={{ gap: '12px' }}>
                <div>
                  <label className="input-label" style={{ fontSize: '11px', marginBottom: '4px' }}>Pre-Test (เต็ม {subject?.max_score_pre})</label>
                  <input
                    id={`input-pre-mobile-${index}`}
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    value={s.pre}
                    onChange={(e) => handleScoreChange(st.id, 'pre', e.target.value)}
                    disabled={isPreLocked() || isInactive}
                    className="input-field"
                    placeholder="ป้อนคะแนน"
                    style={{
                      height: '46px', // Large touch target
                      border: err.pre ? '2px solid var(--danger)' : '1px solid var(--border)',
                      background: (isPreLocked() || isInactive) ? 'rgba(0,0,0,0.05)' : 'var(--bg-input)'
                    }}
                  />
                  {err.pre && <span style={{ color: 'var(--danger)', fontSize: '10px', display: 'block', marginTop: '2px' }}>คะแนนเกินค่าสูงสุด</span>}
                </div>

                <div>
                  <label className="input-label" style={{ fontSize: '11px', marginBottom: '4px' }}>Post-Test (เต็ม {subject?.max_score_post})</label>
                  <input
                    id={`input-post-mobile-${index}`}
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    value={s.post}
                    onChange={(e) => handleScoreChange(st.id, 'post', e.target.value)}
                    disabled={isPostLocked() || isInactive}
                    className="input-field"
                    placeholder="ป้อนคะแนน"
                    style={{
                      height: '46px', // Large touch target
                      border: err.post ? '2px solid var(--danger)' : '1px solid var(--border)',
                      background: (isPostLocked() || isInactive) ? 'rgba(0,0,0,0.05)' : 'var(--bg-input)'
                    }}
                  />
                  {err.post && <span style={{ color: 'var(--danger)', fontSize: '10px', display: 'block', marginTop: '2px' }}>คะแนนเกินค่าสูงสุด</span>}
                </div>
              </div>

              {/* Progress and diff in card */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid var(--border)', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>พัฒนาการสะสม:</span>
                <span style={{ fontWeight: '700', fontSize: '15px' }}>
                  {progress !== null ? (
                    <span style={{ color: progress > 0 ? 'var(--success)' : progress < 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>
                      {progress > 0 ? `+${progress}` : progress} คะแนน
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>—</span>
                  )}
                </span>
              </div>

              {/* Mobile Send Email Button */}
              <button
                type="button"
                onClick={() => handleOpenMailModal(st)}
                className="btn btn-outline"
                style={{ 
                  width: '100%', 
                  padding: '10px 14px', 
                  fontSize: '13px', 
                  fontWeight: '600',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '8px', 
                  borderRadius: '10px' 
                }}
              >
                📧 ส่งเมลแจ้งผลคะแนน
              </button>

            </div>
          )
        })}

        {/* Mobile Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', marginTop: '10px', padding: '16px', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              แสดงนักเรียน {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredStudents.length)} จาก {filteredStudents.length} คน
            </span>
            <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn btn-outline"
                style={{ padding: '8px 16px', fontSize: '13px', borderRadius: '8px', flex: 1 }}
              >
                ◀ ย้อนกลับ
              </button>
              <span style={{ fontSize: '13px', fontWeight: '600', padding: '0 8px', whiteSpace: 'nowrap' }}>
                หน้า {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-outline"
                style={{ padding: '8px 16px', fontSize: '13px', borderRadius: '8px', flex: 1 }}
              >
                ถัดไป ▶
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Classroom statistics summary panel */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px'
      }}>
        <div className="glassmorphism" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500' }}>📈 พัฒนาการเฉลี่ยห้องเรียน (Learning Gain)</span>
          <span style={{ fontSize: '28px', fontWeight: '700', color: Number(stats.avgGain) > 0 ? 'var(--success)' : 'var(--text-primary)' }}>
            {stats.avgGain} คะแนน
          </span>
        </div>
        <div className="glassmorphism" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500' }}>📊 คะแนนเฉลี่ย Pre-Test</span>
          <span style={{ fontSize: '28px', fontWeight: '700', color: 'var(--secondary)' }}>{stats.preAvg} / {subject?.max_score_pre}</span>
        </div>
        <div className="glassmorphism" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500' }}>📊 คะแนนเฉลี่ย Post-Test</span>
          <span style={{ fontSize: '28px', fontWeight: '700', color: 'var(--primary)' }}>{stats.postAvg} / {subject?.max_score_post}</span>
        </div>
      </div>
    </>
  )}

      {activeTab === 'analytics' && (
        <>
          {/* 4. Classroom Summary Dashboard (Graphs, Stats, Reports) */}
          <div className="glassmorphism no-print" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '4px' }}>📊 แดชบอร์ดสรุปสถิติ & รายงานวิเคราะห์ห้องเรียน</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>กราฟเปรียบเทียบผลสัมฤทธิ์ทางการเรียน Pre-Test vs Post-Test และรายงานความก้าวหน้ารายบุคคล</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={() => setShowExportModal(true)} className="btn btn-outline" style={{ fontSize: '13px', padding: '8px 16px', border: '1px solid var(--primary)', color: 'var(--primary)', fontWeight: '600' }}>
              📊 ส่งออก Excel (เลือกหัวข้อ)
            </button>
            <button type="button" onClick={handleExportCSV} className="btn btn-outline" style={{ fontSize: '13px', padding: '8px 16px' }}>
              📥 ส่งออกคะแนนดิบ (CSV)
            </button>
            <button type="button" onClick={() => window.print()} className="btn btn-primary" style={{ fontSize: '13px', padding: '8px 16px' }}>
              🖨️ พิมพ์รายงานสรุปผล
            </button>
          </div>
        </div>

        {/* Score Distribution + Top/Bottom Performers */}
        {(() => {
          const activeSts = students.filter(st => !st.status || st.status === 'active')
          const maxPre = subject?.max_score_pre || 10
          const maxPost = subject?.max_score_post || 10

          // Build score distribution for Post-Test (5 buckets: 0-20%, 21-40%, 41-60%, 61-80%, 81-100%)
          const buckets = [
            { label: '0–20%', color: '#e74c3c', count: 0, pre: 0 },
            { label: '21–40%', color: '#e67e22', count: 0, pre: 0 },
            { label: '41–60%', color: '#f39c12', count: 0, pre: 0 },
            { label: '61–80%', color: '#27ae60', count: 0, pre: 0 },
            { label: '81–100%', color: '#2ecc71', count: 0, pre: 0 },
          ]
          const preDistBuckets = [0, 0, 0, 0, 0]

          activeSts.forEach(st => {
            const s = scores[st.id] || { pre: '', post: '' }
            if (s.post !== '') {
              const pct = (Number(s.post) / maxPost) * 100
              const bi = Math.min(4, Math.floor(pct / 20))
              buckets[bi].count++
            }
            if (s.pre !== '') {
              const pct = (Number(s.pre) / maxPre) * 100
              const bi = Math.min(4, Math.floor(pct / 20))
              preDistBuckets[bi]++
            }
          })

          // Top 5 and Bottom 5 by Post-Test score
          const scoredSts = activeSts
            .map(st => ({ st, post: scores[st.id]?.post !== '' ? Number(scores[st.id]?.post ?? '') : null, pre: scores[st.id]?.pre !== '' ? Number(scores[st.id]?.pre ?? '') : null }))
            .filter(x => x.post !== null) as { st: any; post: number; pre: number | null }[]
          const sorted = [...scoredSts].sort((a, b) => b.post - a.post)
          const top5 = sorted.slice(0, 5)
          const bottom5 = sorted.slice(-5).reverse()

          return (
            <div className="grid-responsive-dashboard" style={{ gap: '20px' }}>

              {/* Distribution Chart */}
              <div style={{ background: 'rgba(255,255,255,0.4)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' }}>
                <span style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  📊 การกระจายคะแนน (จำนวนนักเรียนในแต่ละช่วงคะแนน)
                </span>
                {(() => {
                  const distData = buckets.map((b, i) => ({
                    label: b.label,
                    "Pre-Test": preDistBuckets[i],
                    "Post-Test": b.count
                  }))
                  return (
                    <div style={{ height: '240px', width: '100%', marginTop: '10px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={distData}
                          margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                          <XAxis dataKey="label" stroke="var(--text-secondary)" fontSize={11} tickLine={false} />
                          <YAxis stroke="var(--text-secondary)" fontSize={11} tickLine={false} axisLine={false} />
                          <Tooltip 
                            contentStyle={{ 
                              background: 'var(--bg-card)', 
                              border: '1px solid var(--border)',
                              borderRadius: '8px',
                              color: 'var(--text-primary)' 
                            }} 
                          />
                          <Legend verticalAlign="top" height={36} />
                          <Bar name="Pre-Test" dataKey="Pre-Test" fill="rgba(9,132,227,0.7)" radius={[4, 4, 0, 0]} />
                          <Bar name="Post-Test" dataKey="Post-Test" fill="rgba(16,185,129,0.85)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )
                })()}
              </div>

              {/* Top/Bottom Performers — shown in detail only for teacher/admin; HOD sees summary counts only */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {isHOD ? (
                  /* HOD summary-only view */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Summary Evaluation Cards */}
                    {(() => {
                      const excellent = sorted.filter(x => (x.post / maxPost) * 100 >= 80).length
                      const passing = sorted.filter(x => { const p = (x.post / maxPost) * 100; return p >= 50 && p < 80 }).length
                      const needsWork = sorted.filter(x => (x.post / maxPost) * 100 < 50).length
                      const total = sorted.length
                      return (
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '120px', background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.35)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: '800', color: '#27ae60' }}>{excellent}</div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#27ae60', marginTop: '4px' }}>🏆 ดีเยี่ยม</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>(≥ 80%)</div>
                            {total > 0 && <div style={{ fontSize: '11px', color: '#27ae60', fontWeight: '600', marginTop: '4px' }}>{Math.round((excellent/total)*100)}% ของห้อง</div>}
                          </div>
                          <div style={{ flex: 1, minWidth: '120px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: '800', color: '#3b82f6' }}>{passing}</div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#3b82f6', marginTop: '4px' }}>✅ ผ่าน</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>(50–79%)</div>
                            {total > 0 && <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '600', marginTop: '4px' }}>{Math.round((passing/total)*100)}% ของห้อง</div>}
                          </div>
                          <div style={{ flex: 1, minWidth: '120px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', fontWeight: '800', color: '#e74c3c' }}>{needsWork}</div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#e74c3c', marginTop: '4px' }}>📌 ปรับปรุง</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>(&lt; 50%)</div>
                            {total > 0 && <div style={{ fontSize: '11px', color: '#e74c3c', fontWeight: '600', marginTop: '4px' }}>{Math.round((needsWork/total)*100)}% ของห้อง</div>}
                          </div>
                        </div>
                      )
                    })()}
                    <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', fontStyle: 'italic' }}>
                      * ตัวเลขสรุปจากคะแนน Post-Test เท่านั้น — ข้อมูลรายบุคคลถูกจำกัดการเข้าถึง
                    </p>
                  </div>
                ) : (
                  /* Teacher/Admin detailed view with names */
                  <>
                    {/* Top performers */}
                    <div style={{ background: 'rgba(46,204,113,0.08)', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '12px', padding: '16px' }}>
                      <span style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#27ae60', marginBottom: '12px' }}>
                        🏆 คะแนนดีเยี่ยม (Post-Test สูงสุด 5 คน)
                      </span>
                      {top5.length === 0 ? (
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>ยังไม่มีข้อมูลคะแนน Post-Test</span>
                      ) : top5.map((item, i) => {
                        const pct = Math.round((item.post / maxPost) * 100)
                        return (
                          <div key={item.st.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: i === 0 ? '#f39c12' : i === 1 ? '#95a5a6' : i === 2 ? '#d35400' : 'rgba(39,174,96,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: i < 3 ? '#fff' : '#27ae60', flexShrink: 0 }}>
                              {i + 1}
                            </span>
                            <span style={{ flex: 1, fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.st.title}{item.st.first_name} {item.st.last_name}
                            </span>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#27ae60', whiteSpace: 'nowrap' }}>
                              {item.post}/{maxPost} <span style={{ fontWeight: '400', color: 'var(--text-muted)', fontSize: '11px' }}>({pct}%)</span>
                            </span>
                          </div>
                        )
                      })}
                    </div>

                    {/* Bottom performers */}
                    <div style={{ background: 'rgba(231,76,60,0.07)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: '12px', padding: '16px' }}>
                      <span style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#c0392b', marginBottom: '12px' }}>
                        📌 ควรดูแลเป็นพิเศษ (Post-Test ต่ำสุด 5 คน)
                      </span>
                      {bottom5.length === 0 ? (
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>ยังไม่มีข้อมูลคะแนน Post-Test</span>
                      ) : bottom5.map((item) => {
                        const pct = Math.round((item.post / maxPost) * 100)
                        return (
                          <div key={item.st.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(231,76,60,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: '#e74c3c', flexShrink: 0 }}>
                              !
                            </span>
                            <span style={{ flex: 1, fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.st.title}{item.st.first_name} {item.st.last_name}
                            </span>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#e74c3c', whiteSpace: 'nowrap' }}>
                              {item.post}/{maxPost} <span style={{ fontWeight: '400', color: 'var(--text-muted)', fontSize: '11px' }}>({pct}%)</span>
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>

            </div>
          )
        })()}

        {/* Detailed Classroom Analytical Insights */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          <div className="glassmorphism" style={{ padding: '20px', background: 'rgba(255,255,255,0.3)', border: '1px solid var(--border)' }}>
            <span style={{ display: 'block', fontSize: '13px', color: 'var(--text-muted)', fontWeight: '600', marginBottom: '8px' }}>📉 สรุปอัตราส่วนและประสิทธิผล</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>นักเรียนที่ทำการทดสอบครบ Pre/Post:</span>
                <strong style={{ fontSize: '14px' }}>
                  {students.filter(st => {
                    const s = scores[st.id]
                    return s && s.pre !== '' && s.post !== '' && (!st.status || st.status === 'active')
                  }).length} จาก {students.filter(st => !st.status || st.status === 'active').length} คน
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>อัตราเข้าสอบครบถ้วน (Attendance Rate):</span>
                <strong style={{ fontSize: '14px', color: 'var(--primary)' }}>
                  {students.filter(st => !st.status || st.status === 'active').length > 0
                    ? `${Math.round((students.filter(st => {
                        const s = scores[st.id]
                        return s && s.pre !== '' && s.post !== '' && (!st.status || st.status === 'active')
                      }).length / students.filter(st => !st.status || st.status === 'active').length) * 100)}%`
                    : '0%'}
                </strong>
              </div>
            </div>
          </div>

          <div className="glassmorphism" style={{ padding: '20px', background: 'rgba(255,255,255,0.3)', border: '1px solid var(--border)' }}>
            <span style={{ display: 'block', fontSize: '13px', color: 'var(--text-muted)', fontWeight: '600', marginBottom: '8px' }}>📊 สถิติคะแนนเชิงเปรียบเทียบ</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>คะแนนเฉลี่ยหลังสอบดีขึ้น (Gain Ratio):</span>
                <strong style={{ fontSize: '14px', color: 'var(--success)' }}>
                  +{stats.avgGain} คะแนน
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>นักเรียนที่มีพัฒนาการเป็นบวก (+):</span>
                <strong style={{ fontSize: '14px', color: 'var(--success)' }}>
                  {students.filter(st => {
                    const s = scores[st.id]
                    return s && s.pre !== '' && s.post !== '' && (Number(s.post) - Number(s.pre) > 0) && (!st.status || st.status === 'active')
                  }).length} คน
                </strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )}

      {/* 4. Sticky Footer Zone */}
      <footer style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        background: 'rgba(255, 255, 255, 0.97)',
        backdropFilter: 'blur(16px)',
        borderTop: '1px solid var(--border)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '10px 16px',
        zIndex: 40,
        minHeight: '64px'
      }} className="scoring-footer">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: '1 1 140px' }}>
          <span style={{
            width: '8px', height: '8px',
            borderRadius: '50%',
            background: hasGlobalErrors() ? 'var(--danger)' : 'var(--success)'
          }} />
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            {hasGlobalErrors() ? '❌ พบบันทึกเกินค่าสูงสุด' : '✅ ข้อมูลอยู่ในเกณฑ์ปกติ'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: '1 1 auto', justifyContent: 'flex-end' }}>
          {isHOD ? (
            <span style={{ fontSize: '13px', color: 'var(--warning)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
              🔒 หัวหน้ากลุ่มสาระ — ดูได้เฉพาะภาพรวมสรุปผลเท่านั้น
            </span>
          ) : isReadOnly ? (
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
              ℹ️ คุณกำลังเรียกดูในโหมดอ่านอย่างเดียว (Read-Only) เนื่องจากไม่ใช่ตารางสอนที่คุณครูได้รับมอบหมาย
            </span>
          ) : (
            <>
              {/* Save Draft: disabled when no active session */}
              <button 
                type="button" 
                onClick={handleSaveDraftManual} 
                className="btn btn-outline"
                disabled={isSessionExpired()}
                title={isSessionExpired() ? 'ไม่มีรอบบันทึกคะแนนที่เปิดอยู่' : 'บันทึกแบบร่าง'}
                style={{ 
                  padding: '8px 12px', 
                  fontSize: '13px',
                  opacity: isSessionExpired() ? 0.45 : 1,
                  cursor: isSessionExpired() ? 'not-allowed' : 'pointer'
                }}
              >
                💾 บันทึกแบบร่าง
              </button>
              
              {/* Submit Pre-Test: only show when Pre is not locked (Pre session active, not yet submitted) */}
              {!isPreLocked() && (
                <button 
                  type="button" 
                  onClick={() => setConfirmSubmit('pre')} 
                  disabled={hasGlobalErrors()}
                  className="btn btn-secondary"
                  style={{
                    padding: '8px 12px',
                    fontSize: '13px',
                    opacity: hasGlobalErrors() ? 0.5 : 1,
                    cursor: hasGlobalErrors() ? 'not-allowed' : 'pointer'
                  }}
                >
                  ✓ ส่ง Pre-Test
                </button>
              )}

              {/* Submit Post-Test: only show when Post is not locked (Post session active, not yet submitted) */}
              {!isPostLocked() && (
                <button 
                  type="button" 
                  onClick={() => setConfirmSubmit('post')} 
                  disabled={hasGlobalErrors()}
                  className="btn btn-primary"
                  style={{
                    padding: '8px 12px',
                    fontSize: '13px',
                    opacity: hasGlobalErrors() ? 0.5 : 1,
                    cursor: hasGlobalErrors() ? 'not-allowed' : 'pointer'
                  }}
                >
                  ✓ ส่ง Post-Test
                </button>
              )}
            </>
          )}
        </div>
      </footer>

      {/* Double Confirmation Modal */}
      {confirmSubmit && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          padding: '20px'
        }}>
          <div className="glassmorphism" style={{ padding: '32px', maxWidth: '480px', width: '100%', background: '#ffffff', border: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ⚠️ ยืนยันการส่งคะแนน {confirmSubmit === 'pre' ? 'Pre-Test' : 'Post-Test'}?
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
              การยืนยันการส่งคะแนนสอบจะเป็นการล็อกข้อมูลของห้องเรียนนี้อย่างถาวร 
              คุณครูจะไม่สามารถแก้ไขคะแนนในช่องนี้ได้อีก หากพบปัญหานอกเหนือจากนี้กรุณาแจ้งแอดมินเพื่อปลดล็อกรอบการบันทึก
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmSubmit(null)} className="btn btn-outline">ยกเลิก</button>
              <button 
                onClick={() => handleSubmitFinal(confirmSubmit)} 
                className="btn btn-secondary"
                style={{ padding: '10px 20px', fontWeight: '700' }}
              >
                ยืนยันและล็อกคะแนน
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Teacher Score Unlock Request Modal */}
      {showUnlockModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          padding: '20px'
        }}>
          <div className="glassmorphism" style={{ padding: '32px', maxWidth: '480px', width: '100%', background: '#ffffff', border: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🔓 คำร้องขอปลดล็อกแก้ไขคะแนน {showUnlockModal === 'pre' ? 'Pre-Test' : 'Post-Test'}
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.5', marginBottom: '20px' }}>
              กรุณาระบุเหตุผลชี้แจงความจำเป็นในการขอปลดล็อกแก้ไขคะแนนสอบห้องเรียนนี้ต่อผู้ดูแลระบบหรือหัวหน้ากลุ่มสาระ
            </p>
            
            <form onSubmit={handleSendUnlockRequest} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label className="input-label" htmlFor="unlockReasonInput">เหตุผลการขอแก้ไขคะแนน</label>
                <textarea
                  id="unlockReasonInput"
                  rows={4}
                  required
                  placeholder="ตัวอย่างเช่น: มีข้อผิดพลาดในข้อมูลต้นฉบับ หรือเด็กกรอกสลับรหัส..."
                  value={unlockReason}
                  onChange={(e) => setUnlockReason(e.target.value)}
                  className="input-field"
                  style={{ width: '100%', resize: 'none', padding: '12px', background: 'var(--bg-input)' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button type="button" onClick={() => setShowUnlockModal(null)} className="btn btn-outline">ยกเลิก</button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  style={{ padding: '10px 20px', fontWeight: '700' }}
                >
                  ✓ ส่งคำร้องชี้แจง
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. Custom Excel Export Selection Modal */}
      {showExportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div className="glassmorphism animate-in fade-in" style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '520px',
            padding: '32px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                📊 ส่งออกรายงาน Excel (กำหนดหัวข้อเอง)
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                เลือกหัวข้อคอลัมน์ที่คุณต้องการพิมพ์หรือรวมในไฟล์ Excel ตามรูปแบบที่คุณต้องการ
              </p>
            </div>

            <div className="grid-responsive-2" style={{ gap: '14px', margin: '8px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.studentCode} onChange={(e) => setSelectedColumns({ ...selectedColumns, studentCode: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                รหัสประจำตัวนักเรียน
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.no} onChange={(e) => setSelectedColumns({ ...selectedColumns, no: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                เลขที่
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.name} onChange={(e) => setSelectedColumns({ ...selectedColumns, name: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                ชื่อ-นามสกุล
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.scorePre} onChange={(e) => setSelectedColumns({ ...selectedColumns, scorePre: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                คะแนน Pre-Test
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.percentPre} onChange={(e) => setSelectedColumns({ ...selectedColumns, percentPre: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                ร้อยละ Pre-Test
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.scorePost} onChange={(e) => setSelectedColumns({ ...selectedColumns, scorePost: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                คะแนน Post-Test
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.percentPost} onChange={(e) => setSelectedColumns({ ...selectedColumns, percentPost: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                ร้อยละ Post-Test
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.gain} onChange={(e) => setSelectedColumns({ ...selectedColumns, gain: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                พัฒนาการ (Learning Gain)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.gainPercent} onChange={(e) => setSelectedColumns({ ...selectedColumns, gainPercent: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                ร้อยละความก้าวหน้า
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                <input type="checkbox" checked={selectedColumns.evaluation} onChange={(e) => setSelectedColumns({ ...selectedColumns, evaluation: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                ผลการประเมินผู้เรียน
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
              <button type="button" onClick={() => setShowExportModal(false)} className="btn btn-outline" style={{ padding: '10px 20px' }}>ยกเลิก</button>
              <button 
                type="button" 
                onClick={handleExportCustomExcel} 
                className="btn btn-primary"
                style={{ padding: '10px 20px', fontWeight: '700' }}
              >
                📥 ดาวน์โหลดไฟล์ Excel (CSV)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gmail Compose and Send Modal */}
      {showMailModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 101,
          padding: '20px'
        }}>
          <div className="glassmorphism" style={{ padding: '32px', maxWidth: '560px', width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
              📧 ส่งผลคะแนนสอบผ่าน Gmail API
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '20px' }}>
              ส่งผลคะแนนสอบ Pre-Test / Post-Test ส่วนตัวโดยตรงหาผู้ปกครองหรือนักเรียน: <strong>{showMailModal.first_name} {showMailModal.last_name}</strong>
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label className="input-label" htmlFor="emailTargetInput">อีเมลผู้รับ</label>
                <input
                  id="emailTargetInput"
                  type="email"
                  placeholder="ตัวอย่าง: student@school.ac.th"
                  value={emailTarget}
                  onChange={(e) => setEmailTarget(e.target.value)}
                  className="input-field"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label className="input-label" htmlFor="emailSubjectInput">หัวข้อจดหมาย</label>
                <input
                  id="emailSubjectInput"
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="input-field"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label className="input-label" htmlFor="emailBodyInput">รายละเอียดเนื้อหา</label>
                <textarea
                  id="emailBodyInput"
                  rows={8}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  className="input-field"
                  style={{ width: '100%', resize: 'none', fontFamily: 'inherit', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button type="button" onClick={() => setShowMailModal(null)} className="btn btn-outline" disabled={sendingEmail}>ยกเลิก</button>
                <button
                  type="button"
                  onClick={sendGmailNotification}
                  className="btn btn-primary"
                  disabled={sendingEmail}
                  style={{ padding: '10px 20px', fontWeight: '700' }}
                >
                  {sendingEmail ? 'กำลังส่งจดหมาย...' : '✈ ส่งผ่าน Gmail'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Styles injection for layout switching and footer alignment */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-only { display: none !important; }
          .mobile-only { display: flex !important; }
          .main-content-layout main { padding: 16px !important; }
          .scoring-footer {
            left: 0 !important;
            padding: 0 16px !important;
          }
        }
        @media (min-width: 1024px) {
          .scoring-footer {
            left: 260px !important; /* aligned with sidebar */
          }
        }
      `}</style>

    </div>
  )
}
export default ScoringGrid
